import { useCampaignCode } from '@campaign-services/hooks/useCampaignCode'
import { useDialog } from '@dni-hooks/useDialog'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { type CheckoutOrder } from '@hooks-api/types/dataModel/orders'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { Input } from '@pets-ui/Input/Input'
import { ResponsiveDialog } from '@pets-ui/ResponsiveDialog/ResponsiveDialog'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { isEmail } from '@utils'
import { useEffect, useRef } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Asterisk } from '../../../../../components/Asterisk/Asterisk'
import { partnerDisable } from '../../../../fp-b2b-shared/utils/partner-disable'
export const DIALOG_KEY = 'DialogMerchantInfo'

interface MerchantInfoObject {
  billing_address_first_name: CheckoutOrder['billing_address_first_name']
  billing_address_email: CheckoutOrder['billing_address_email']
  billing_address_phone: CheckoutOrder['billing_address_phone']
}

export function DialogMerchantInfo() {
  const { open, closeDialog } = useDialog(DIALOG_KEY)
  const { watch, setValue, trigger, clearErrors, formState: { errors } } = useFormContext<CheckoutOrder>()
  const formValues = watch()
  const merchantInfoObjectRef = useRef<MerchantInfoObject>({
    billing_address_first_name: formValues?.billing_address_first_name,
    billing_address_email: formValues?.billing_address_email,
    billing_address_phone: formValues?.billing_address_phone,
  })

  useEffect(() => {
    if (open) return
    merchantInfoObjectRef.current = {
      billing_address_first_name: formValues?.billing_address_first_name,
      billing_address_email: formValues?.billing_address_email,
      billing_address_phone: formValues?.billing_address_phone,
    }
  }, [formValues, open])

  const handleReset = () => {
    setValue('billing_address_first_name', merchantInfoObjectRef.current.billing_address_first_name)
    setValue('billing_address_email', merchantInfoObjectRef.current.billing_address_email)
    setValue('billing_address_phone', merchantInfoObjectRef.current.billing_address_phone)
  }

  const handleClose = () => {
    handleReset()
    closeDialog()
  }

  const handleSubmit = async () => {
    await trigger(['billing_address_first_name', 'billing_address_email', 'billing_address_phone'])
    if (errors?.billing_address_first_name || errors?.billing_address_email || errors?.billing_address_phone) return
    clearErrors('merchantInfoValidation')
    closeDialog()
  }

  return (
    <ResponsiveDialog open={open} fullWidth>
      <DialogHeader title="經銷商資訊" onClose={handleClose} />
      <DialogMerchantInfoContent />
      <Footer onSubmit={handleSubmit} />
    </ResponsiveDialog>
  )
}

const dialogAndDrawer = stylex.create({
  root: {
    padding: 16,
  },
})

function DialogMerchantInfoContent() {
  const { watch, control } = useFormContext<CheckoutOrder>()
  const formValues = watch()
  const { campaignCodeFromUrl } = useCampaignCode()
  const { destructuredData: userInfo } = useApiCampaignMember({ code: campaignCodeFromUrl })
  const isPartner = partnerDisable({ roles: userInfo?.roles || [] })

  return (
    <Box rootStyle={dialogAndDrawer.root}>
      <VStack gap={4}>
        <VStack gap={2}>
          <Text>
            經銷商名稱
            <Asterisk />
          </Text>
          <Controller
            control={control}
            name="billing_address_first_name"
            rules={{
              required: '經銷商欄位不得為空',
            }}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Input
                {...field}
                rootRef={ref}
                onChange={field.onChange}
                value={formValues.billing_address_first_name}
                placeholder="請輸入經銷商"
                error={!!error}
                helpText={error?.message}
                fullWidth
                disabled={isPartner}
                maxLength={30}
              />
            )}
          />
        </VStack>
        <VStack gap={2}>
          <Text>
            Email
            <Asterisk />
          </Text>
          <Controller
            control={control}
            name="billing_address_email"
            rules={{
              required: '電子信箱不得為空！',
              validate: value => isEmail(value) ? true : '電子信箱格式錯誤！',
            }}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Input
                {...field}
                rootRef={ref}
                onChange={field.onChange}
                value={formValues.billing_address_email}
                placeholder="請輸入電子信箱"
                error={!!error}
                helpText={error?.message}
                fullWidth
                disabled={isPartner}
              />
            )}
          />
        </VStack>
        <VStack gap={2}>
          <Text>
            電話
            <Asterisk />
          </Text>
          <Controller
            control={control}
            name="billing_address_phone"
            rules={{
              required: '號碼不得為空',
            }}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Input
                {...field}
                rootRef={ref}
                onChange={field.onChange}
                value={formValues.billing_address_phone}
                placeholder="請輸入號碼"
                error={!!error}
                helpText={error?.message}
                fullWidth
              />
            )}
          />
        </VStack>
      </VStack>
    </Box>
  )
}

const footer = stylex.create({
  root: {
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    backgroundColor: colors.black,
    color: colors.white,
  },
})

function Footer({ onSubmit }: { onSubmit: () => void }) {
  const isMobile = useResponsive('mobile')
  return (
    <Box rootStyle={footer.root}>
      <Button variant="filled" rootStyle={footer.button} onClick={onSubmit} fullWidth={isMobile}>
        確認
      </Button>
    </Box>
  )
}
